import type { NoteScope } from "@sebas/core";
import { Check, ChevronDown, Globe, Inbox, Lock, Users } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/cn";
import { useTeams, type TeamSummary } from "@/lib/teams";

/**
 * Which library the Notes screen is showing.
 *
 * Scope and team travel together because "team" means nothing without one; a
 * single value keeps them from drifting apart in component state.
 */
export type NoteScopeSelection = { scope: NoteScope; teamId?: string };

/** Personal notes — what you see before you choose anything else. */
export const DEFAULT_SCOPE: NoteScopeSelection = { scope: "personal" };

/** Stable string form, so the selection can key a list and compare with `===`. */
const keyOf = (s: NoteScopeSelection) => (s.scope === "team" ? `team:${s.teamId}` : s.scope);

type Choice = {
  key: string;
  label: string;
  hint: string;
  icon: typeof Lock;
  value: NoteScopeSelection;
};

function choicesFor(teams: TeamSummary[]): Choice[] {
  return [
    {
      key: "personal",
      label: "My notes",
      hint: "Just yours",
      icon: Lock,
      value: { scope: "personal" },
    },
    ...teams.map(
      (t): Choice => ({
        key: `team:${t.id}`,
        label: t.name,
        hint: t.role,
        icon: Users,
        value: { scope: "team", teamId: t.id },
      }),
    ),
    {
      key: "shared",
      label: "Shared with me",
      hint: "Notes you accepted",
      icon: Inbox,
      value: { scope: "shared" },
    },
    {
      key: "all",
      label: "All notes",
      hint: "Everything above",
      icon: Globe,
      value: { scope: "all" },
    },
  ];
}

/**
 * The Notes screen's title, and the control that changes it.
 *
 * The heading *is* the button rather than sitting next to one: there is only
 * ever one library on screen, so naming it and switching it are the same act.
 */
export function ScopePicker({
  value,
  onChange,
}: {
  value: NoteScopeSelection;
  onChange: (next: NoteScopeSelection) => void;
}) {
  const { data } = useTeams();
  const teams = useMemo(() => data?.teams ?? [], [data]);
  const choices = useMemo(() => choicesFor(teams), [teams]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const selectedKey = keyOf(value);
  const current = choices.find((c) => c.key === selectedKey);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onPointer = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onPointer);
    };
  }, [open]);

  return (
    <div ref={boxRef} className="relative w-fit max-w-full">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex max-w-full items-center gap-3 rounded-card text-left",
          "font-display text-4xl font-semibold text-ink sm:text-5xl lg:text-6xl",
          "outline-none transition-colors hover:text-ink-muted",
          "focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2",
        )}
      >
        <span className="min-w-0 truncate">{current?.label ?? "Notes"}</span>
        <ChevronDown
          className={cn(
            "h-6 w-6 shrink-0 text-ink-muted transition-transform lg:h-7 lg:w-7",
            open && "rotate-180",
          )}
        />
      </button>

      {open ? (
        <div
          role="menu"
          className={cn(
            "absolute left-0 top-full z-30 mt-2 w-[min(20rem,calc(100vw-2.5rem))]",
            "overflow-hidden rounded-card border border-line bg-raised py-1 shadow-card",
            "motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95",
          )}
        >
          {choices.map((c, i) => {
            const active = c.key === selectedKey;
            // The teams sit directly under "My notes"; the two cross-cutting
            // views below the rule are a different kind of thing.
            const rule = c.key === "shared" && i > 0;
            return (
              <div key={c.key}>
                {rule ? <div className="my-1 border-t border-line" /> : null}
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  onClick={() => {
                    onChange(c.value);
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors",
                    active ? "bg-sunken" : "hover:bg-sunken/60",
                  )}
                >
                  <c.icon className="h-4 w-4 shrink-0 text-ink-muted" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-ink">{c.label}</span>
                    <span className="block truncate text-xs text-ink-faint">{c.hint}</span>
                  </span>
                  {active ? <Check className="h-4 w-4 shrink-0 text-accent" /> : null}
                </button>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
